import { create } from 'zustand';

/**
 * UI Store — SPEC-FRONTEND_V4 §12.
 * Extended with V4 shell state (topbar selectors, view router flags,
 * right-nav panel, customize modal). Legacy fields retained for V3 panels.
 */

export type TopbarOrg = 'Federal' | 'State' | 'Local' | 'Private';
export type ActiveView = 'home' | 'function' | 'tool';

interface UiState {
  // V3 legacy
  sidebarCollapsed: boolean;
  feedbackOpen: boolean;
  activePanel: string;

  /** Sidebar "Functions" category collapse state. Functions list is
   *  intentionally collapsed by default (per the rail redesign) so
   *  Core + Advanced get the user's first attention; one click on the
   *  Functions header expands the list with bevel buttons matching
   *  Core/Advanced's projected-popover hover behavior. */
  functionsExpanded: boolean;

  // V4 shell
  topbarOrg: TopbarOrg;
  topbarMode: string;
  activeView: ActiveView;
  selectedFunctionKey: string | null;
  selectedToolId: string | null;
  rNavPanelOpen: boolean;
  activeRNavItem: string | null;
  customizeModalOpen: boolean;
  notificationsOpen: boolean;

  // V3 actions
  toggleSidebar: () => void;
  toggleFeedback: () => void;
  setActivePanel: (panel: string) => void;
  toggleFunctionsExpanded: () => void;

  // V4 actions
  setTopbarOrg: (org: TopbarOrg) => void;
  setTopbarMode: (mode: string) => void;
  setActiveView: (view: ActiveView) => void;
  setSelectedFunction: (key: string | null) => void;
  setSelectedTool: (id: string | null) => void;
  toggleRNavPanel: (item: string | null) => void;
  openCustomizeModal: () => void;
  closeCustomizeModal: () => void;
  toggleNotifications: () => void;
}

export const useUiStore = create<UiState>((set) => ({
  // V3 legacy
  sidebarCollapsed: false,
  feedbackOpen: false,
  activePanel: 'dashboard',
  functionsExpanded: false,

  // V4 defaults
  topbarOrg: 'Federal',
  topbarMode: 'all',
  activeView: 'home',
  selectedFunctionKey: null,
  selectedToolId: null,
  rNavPanelOpen: false,
  activeRNavItem: null,
  customizeModalOpen: false,
  notificationsOpen: false,

  toggleSidebar: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  toggleFeedback: () =>
    set((state) => ({ feedbackOpen: !state.feedbackOpen })),
  setActivePanel: (activePanel) => set({ activePanel }),
  toggleFunctionsExpanded: () =>
    set((state) => ({ functionsExpanded: !state.functionsExpanded })),

  setTopbarOrg: (topbarOrg) => set({ topbarOrg }),
  setTopbarMode: (topbarMode) => set({ topbarMode }),
  setActiveView: (activeView) => set({ activeView }),
  setSelectedFunction: (selectedFunctionKey) => set({ selectedFunctionKey }),
  setSelectedTool: (selectedToolId) => set({ selectedToolId }),
  toggleRNavPanel: (item) =>
    set({ rNavPanelOpen: item !== null, activeRNavItem: item }),
  openCustomizeModal: () => set({ customizeModalOpen: true }),
  closeCustomizeModal: () => set({ customizeModalOpen: false }),
  toggleNotifications: () =>
    set((state) => ({ notificationsOpen: !state.notificationsOpen })),
}));
